import api from "@/libs/axios";
import type {
  Notification,
  NotificationListParams,
  NotificationListResponse,
  UnreadCountResponse,
} from "@/types/notification";

/** Normalised result returned by {@link getNotifications}. */
export interface NotificationListResult {
  notifications: Notification[];
  unreadCount: number;
  pagination: NotificationListResponse["pagination"];
}

/** GET /api/notifications — paginated list (pass `unread: true` for unread only). */
export async function getNotifications(
  params: NotificationListParams = {},
  signal?: AbortSignal,
): Promise<NotificationListResult> {
  const res = await api.get<NotificationListResponse>("/api/notifications", {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      // omit `unread` entirely when not requesting unread-only
      unread: params.unread ? true : undefined,
    },
    signal,
  });

  return {
    notifications: res.data.data ?? [],
    unreadCount: res.data.unreadCount ?? 0,
    pagination: res.data.pagination,
  };
}

/** GET /api/notifications/unread-count — seed/refresh the badge. */
export async function getUnreadCount(signal?: AbortSignal): Promise<number> {
  const res = await api.get<UnreadCountResponse>(
    "/api/notifications/unread-count",
    { signal },
  );
  return res.data.data?.count ?? 0;
}

/** PATCH /api/notifications/:id/read — mark a single notification read. */
export async function markNotificationRead(
  id: number | string,
): Promise<void> {
  await api.patch(`/api/notifications/${id}/read`);
}

/** PATCH /api/notifications/read-all — mark every notification read. */
export async function markAllNotificationsRead(): Promise<void> {
  await api.patch("/api/notifications/read-all");
}

/** DELETE /api/notifications/:id — delete a single notification. */
export async function deleteNotification(id: number | string): Promise<void> {
  await api.delete(`/api/notifications/${id}`);
}

/** DELETE /api/notifications — clear ALL notifications. */
export async function clearAllNotifications(): Promise<void> {
  await api.delete("/api/notifications");
}
